/* eslint-disable */
import { useEffect, useState } from "react";
import api from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function Sparkline({ daily }) {
  // Simple inline SVG bar chart of the last 30 days
  if (!daily || daily.length === 0) return null;
  const max = Math.max(1, ...daily.map(d => d.clicks));
  const w = 600, h = 80, barW = w / daily.length;
  return (
    <svg viewBox={`0 0 ${w} ${h + 22}`} preserveAspectRatio="none" className="m-utm-spark" data-testid="utm-spark">
      {daily.map((d, i) => {
        const bh = (d.clicks / max) * h;
        const x = i * barW + 2;
        const y = h - bh;
        return (
          <g key={d.day}>
            <rect x={x} y={y} width={Math.max(2, barW - 4)} height={bh} fill="#B8913A" rx="1.5">
              <title>{d.day}: {d.clicks} clicks</title>
            </rect>
          </g>
        );
      })}
      {/* axis labels: first, midpoint, last day */}
      {[0, Math.floor(daily.length / 2), daily.length - 1].map(i => (
        <text key={i} x={i * barW + barW / 2} y={h + 14} textAnchor="middle" fontSize="9" fill="#6b6b6b" fontFamily="Jost,sans-serif">
          {daily[i].day.slice(5)}
        </text>
      ))}
    </svg>
  );
}

export default function AdminUtm() {
  const [stats, setStats] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/utm/stats"),
        api.get("/admin/utm/clicks?limit=100"),
      ]);
      setStats(s.data);
      setClicks(c.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load UTM stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="m-loading-inline" data-testid="utm-loading">Loading UTM stats…</div>;
  if (err) return <div className="m-empty" data-testid="utm-error">{err}</div>;
  if (!stats) return null;

  return (
    <div className="m-admin-section" data-testid="utm-panel">
      {/* Top-line stat strip */}
      <div className="m-stats-row m-utm-stats">
        <div className="m-stat" data-testid="utm-total">
          <span className="m-stat-num">{stats.total_clicks}</span>
          <span className="m-stat-label">Total outclicks</span>
        </div>
        <div className="m-stat" data-testid="utm-7d">
          <span className="m-stat-num">{stats.clicks_7d}</span>
          <span className="m-stat-label">Last 7 days</span>
        </div>
        <div className="m-stat" data-testid="utm-30d">
          <span className="m-stat-num">{stats.clicks_30d}</span>
          <span className="m-stat-label">Last 30 days</span>
        </div>
        <div className="m-stat" data-testid="utm-workspaces">
          <span className="m-stat-num">{stats.by_workspace.length}</span>
          <span className="m-stat-label">Listings clicked</span>
        </div>
      </div>

      {/* 30-day daily sparkline */}
      <div className="m-card m-utm-card">
        <div className="m-side-title">Outclicks · <em>last 30 days</em></div>
        <Sparkline daily={stats.daily} />
      </div>

      <div className="m-utm-grid">
        {/* By workspace */}
        <div className="m-card m-utm-card">
          <div className="m-side-title">By <em>listing</em></div>
          {stats.by_workspace.length === 0 ? (
            <div className="m-muted small">No outclicks recorded yet. Once members start clicking <strong>Book direct</strong>, you&apos;ll see them here.</div>
          ) : (
            <table className="m-utm-table" data-testid="utm-by-workspace">
              <thead><tr><th>Listing</th><th>City</th><th style={{textAlign:"right"}}>Clicks</th><th>Last click</th></tr></thead>
              <tbody>
                {stats.by_workspace.map(r => (
                  <tr key={r.workspace_slug} data-testid={`utm-ws-${r.workspace_slug}`}>
                    <td>{r.workspace_name}</td>
                    <td className="m-muted">{r.workspace_city}</td>
                    <td style={{textAlign:"right",fontVariantNumeric:"tabular-nums"}}><strong>{r.clicks}</strong></td>
                    <td className="m-muted small">{fmtDate(r.last_click)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* By source */}
        <div className="m-card m-utm-card">
          <div className="m-side-title">By <em>source page</em></div>
          {stats.by_source.length === 0 ? (
            <div className="m-muted small">—</div>
          ) : (
            <table className="m-utm-table" data-testid="utm-by-source">
              <thead><tr><th>Source</th><th style={{textAlign:"right"}}>Clicks</th></tr></thead>
              <tbody>
                {stats.by_source.map(r => (
                  <tr key={r.source}>
                    <td>/{r.source}</td>
                    <td style={{textAlign:"right",fontVariantNumeric:"tabular-nums"}}><strong>{r.clicks}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Raw log */}
      <div className="m-card m-utm-card">
        <div className="m-side-title">Recent <em>clicks</em></div>
        {clicks.length === 0 ? (
          <div className="m-muted small">No clicks recorded yet.</div>
        ) : (
          <table className="m-utm-table" data-testid="utm-recent">
            <thead><tr><th>When</th><th>Listing</th><th>Source</th><th>Member</th></tr></thead>
            <tbody>
              {clicks.map(c => (
                <tr key={c.click_id}>
                  <td className="m-muted small">{fmtDate(c.created_at)}</td>
                  <td>{c.workspace_name}</td>
                  <td className="m-muted">/{c.source}</td>
                  <td className="m-muted small">{c.user_email || "anon"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
